// Modules
import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule, Optional, SkipSelf } from '@angular/core';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { SharedModule } from '../shared/shared.module';
import { CoreRoutingModule } from './core-routing.module';
import { ReactiveFormsModule } from '@angular/forms';


// Components
import { AlertComponent } from './alert/alert.component';
import { EnvironmentComponent } from './environment/environment.component';
import { HeaderComponent } from './header/header.component';
import { SidebarComponent } from './sidebar/sidebar.component';
import { LoginComponent } from './login/login.component';
import { NotFoundComponent } from './not-found/not-found.component';
import { OcidComponent } from './ocid/ocid.component';
import { UnavailableComponent } from './unavailable/unavailable.component';
import { UtilityNavigationComponent } from './utility-navigation/utility-navigation.component';
import { NotificationsComponent } from './notifications/notifications.component';
import { DashboardHelpComponent } from './help/dashboard/dashboard-help.component';
import { NotFoundHelpComponent } from './help/not-found/not-found-help.component';
import { MyCoursesHelpComponent } from './help/my-courses/my-courses-help.component';
import { CalendarHelpComponent } from './help/calendar/calendar-help.component';
import { SidebarContentDirective } from './sidebar/sidebar-content.directive';
import { CanvasHelpComponent } from './help/canvas/canvas-help.component';
import { JobPostingsHelpComponent } from './help/job-postings/job-postings-help.component';
import { EventsHelpComponent } from './help/events/events-help.component';
import { BiddingHelpComponent } from './help/bidding/bidding-help.component';
import { DropAddHelpComponent } from './help/drop-add/drop-add-help.component';
import { ErrorModalComponent } from './modals/error-modal/error-modal.component';
import { CurrentBidHelpComponent } from './help/bidding/currentbid-help.component';
import { LostBidHelpComponent } from './help/bidding/lostbid-help.component';
import { WonBidHelpComponent } from './help/bidding/wonbid-help.component';
import { BidCalendarHelpComponent } from './help/bidding/bidcalendar-help.component';
import { BidPointsHelpComponent } from './help/bidding/bidpoints-help.component';
import { BidTimelineHelpComponent } from './help/bidding/bidtimeline-help.component';
import { TransferCreditsHelpComponent } from './help/transfer-credits/transfer-credits-help.component'
import { CatalogHelpComponent } from './help/catalog/catalog-help.component';
import { ConcentrationsModalComponent } from './modals/concentrations/concentrations.component';
import { MyDegreeHelpComponent } from './help/my-degree/my-degree-help.component';
import { FacultyCoursesHelpComponent } from './help/faculty-courses/faculty-courses-help.component';
import { NgxMasonryModule, NgxMasonryComponent } from 'ngx-masonry';
import { DisplayDetailsModalComponent } from './modals/display-details/display-details.component';
import { TileModalComponent } from './modals/tile-modal/tile-modal.component';
import { SolarWindsEquipmentTemplate } from '../widgets/my-equipment/solarwinds-equipment/solarwinds-equipment.component';
import { MsrRecordingRequestsComponent } from '../widgets/my-support-requests/tabs/recording-requests/msr-recording-requests.component';
import { RecordingApprovalModalComponent } from '../widgets/my-support-requests/tabs/recording-requests/recording-approval-modal/recording-approval-modal.component';


// Widgets
import { AgendaComponent } from '../widgets/agenda/agenda.component';
import { AnnouncementsComponent } from '../widgets/announcements/announcements.component';
import { CalendarComponent } from '../widgets/calendar/calendar.component';
import { ContactsComponent } from '../widgets/contacts/contacts.component';
import { CmsComponent } from 'src/app/widgets/content/cms.component';
import { MySupportRequestsComponent } from '../widgets/my-support-requests/my-support-requests.component';
import { MyCoursesConflictsModalComponent } from '../widgets/my-courses/conflicts-modal/my-courses-conflicts.component';
import { MyCoursesRecordingRequestModalComponent } from '../widgets/my-courses/recording-request-modal/my-courses-recording-request.component';
import { MyCoursesComponent } from '../widgets/my-courses/my-courses.component';
import { PointsTrackingComponent } from '../widgets/points-tracking/points-tracking.component';
import { MyDegreeComponent } from '../widgets/my-degree/my-degree.component';
import { HasAnyPipe, TransferCreditsComponent } from '../widgets/transfer-credits/transfer-credits.component';
import { KudosComponent } from '../widgets/kudos/kudos.component';
import { MyLinksComponent } from '../widgets/my-links/my-links.component';
import { MyDocumentsComponent } from '../widgets/my-documents/my-documents.component';
import { MyFavoritesComponent } from '../widgets/my-favorites/my-favorites.component';
import { MyEquipmentComponent } from '../widgets/my-equipment/my-equipment.component';
import { TilePageComponent } from '../widgets/tile-page/tile-page.component';
import { MyPagesComponent } from '../widgets/my-pages/my-pages.component';
import { JobPostingsComponent } from '../widgets/careers/job-postings/job-postings.component';
import { EventsComponent } from '../widgets/careers/events/events.component';
import { CareerGridDisplayComponent } from '../widgets/careers/grid-display/career-grid-display.component';
import { CanvasAssignmentsComponent } from '../widgets/canvas-assignments/canvas-assignments.component';
import { FacultyCoursesComponent } from '../widgets/faculty-courses/faculty-courses.component';

// Services
import { AnnouncementsService } from '../services/announcements.service';
import { CalendarService } from '../services/calendar.service';
import { CareersService } from '../services/careers.service';
import { PointsTrackingService } from '../services/points-tracking.service';
import { LoggerService } from './services/logger.service';
import { ProfileService } from './services/profile.service';
import { CmsService } from './services/cms.service';
import { TagManagerService } from './services/tag-manager.service';
import { UtilityService } from './services/utility.service';
import { EnrollmentsService } from '../services/enrollments.service';
import { BiddingService } from '../services/bidding.service';
import { DeactivateGuardService } from './services/deactivate-guard.service';
import { FacultyService } from '../services/faculty.service';
import { CanvasService } from '../services/canvas.service';
import { MyDegreeSimpleProgressBarComponent } from '../widgets/my-degree/simple-progress-bar/my-degree-simple-progress-bar.component';
import { MyDegreeDetailProgressBarComponent } from '../widgets/my-degree/detail-progress-bar/my-degree-detail-progress-bar.component';

// Pipes
import { UppercaseSlashPipe } from '../shared/uppercase.pipe';
import { DayFilterPipe } from '../shared/date.pipe';
import { NewRequestComponent } from '../widgets/transfer-credits/new-request/new-request.component';
import { ProgramFinderComponent } from '../widgets/transfer-credits/program-finder/program-finder.component';
import { EditRequestModalComponent } from '../widgets/transfer-credits/edit-request-modal/edit-request-modal.component';
import { ApprovalNewRequestListComponent } from '../widgets/transfer-credits/approval-new-request-list/approval-new-request-list.component';
import { ApprovalEditRequestComponent } from '../widgets/transfer-credits/approval-edit-request/approval-edit-request.component';
import { FacultySyllabusClassFilterPipe, FacultySyllabusFilterPipe, SyllabusUpdateModalComponent } from '../widgets/faculty-courses/syllabus-update-modal/syllabus-update-modal.component';
import { MsrTableauComponent } from '../widgets/my-support-requests/tabs/tableau/msr-tableau.component';
import { MsrPurchasingComponent } from '../widgets/my-support-requests/tabs/purchasing/msr-purchasing.component';

@NgModule({
    imports: [
        CommonModule,
        CoreRoutingModule,
        NgbModule,
        NgbTooltipModule,
        SharedModule,
        NgxMasonryModule,
        ReactiveFormsModule
    ],
    declarations: [
        LoginComponent,
        HeaderComponent,
        SidebarComponent,
        NotFoundComponent,
        AlertComponent,
        EnvironmentComponent,
        OcidComponent,
        UnavailableComponent,
        UppercaseSlashPipe,       
        DashboardHelpComponent,
        NotFoundHelpComponent,
        MyCoursesHelpComponent,
        CalendarHelpComponent,
        CanvasHelpComponent,
        JobPostingsHelpComponent,
        EventsHelpComponent,
        BiddingHelpComponent,
        CurrentBidHelpComponent,
        LostBidHelpComponent,
        WonBidHelpComponent,
        BidCalendarHelpComponent,
        BidPointsHelpComponent,
        BidTimelineHelpComponent,
        DropAddHelpComponent,
        SidebarContentDirective,
        ErrorModalComponent,
        TransferCreditsHelpComponent,
        CatalogHelpComponent,
        ConcentrationsModalComponent,
        MyDegreeHelpComponent,
        FacultyCoursesHelpComponent,
        SyllabusUpdateModalComponent,
        NotificationsComponent,
        TileModalComponent,
        UtilityNavigationComponent,
        DisplayDetailsModalComponent,
        AgendaComponent,
        AnnouncementsComponent,
        CalendarComponent,
        ContactsComponent,
        CmsComponent,
        MySupportRequestsComponent,
        MsrTableauComponent,
        MsrPurchasingComponent,
        MyCoursesConflictsModalComponent,
        MyCoursesRecordingRequestModalComponent,
        MyCoursesComponent,
        PointsTrackingComponent,
        MyDegreeComponent,
        TransferCreditsComponent,
        NewRequestComponent,
        ProgramFinderComponent,
        EditRequestModalComponent,
        ApprovalNewRequestListComponent,
        ApprovalEditRequestComponent,
        KudosComponent,
        MyLinksComponent,
        MyDocumentsComponent,
        MyFavoritesComponent,
        MyEquipmentComponent,
        TilePageComponent,
        MyPagesComponent,
        JobPostingsComponent,
        EventsComponent,
        CareerGridDisplayComponent,
        CanvasAssignmentsComponent,
        FacultyCoursesComponent,       
        MyDegreeSimpleProgressBarComponent,
        MyDegreeDetailProgressBarComponent,
        FacultySyllabusFilterPipe,
        FacultySyllabusClassFilterPipe,
        SolarWindsEquipmentTemplate,
        HasAnyPipe,
        MsrRecordingRequestsComponent,
        RecordingApprovalModalComponent,
    ],
    exports: [
        HeaderComponent,        
        SidebarComponent,
        AlertComponent,
        EnvironmentComponent,
        SidebarContentDirective,
        NotFoundHelpComponent,
        CalendarHelpComponent,
        CanvasHelpComponent,
        JobPostingsHelpComponent,
        EventsHelpComponent,
        BiddingHelpComponent,
        CurrentBidHelpComponent,
        LostBidHelpComponent,
        WonBidHelpComponent,
        BidCalendarHelpComponent,
        BidPointsHelpComponent,
        BidTimelineHelpComponent,
        DropAddHelpComponent,
        MyCoursesHelpComponent,
        NgbTooltipModule,
        TransferCreditsHelpComponent,
        CatalogHelpComponent,
        MyDegreeHelpComponent,
        FacultyCoursesHelpComponent,
        NgxMasonryComponent,
        HasAnyPipe,
    ],
    providers: [
        DayFilterPipe,        
        FacultySyllabusFilterPipe,
        FacultySyllabusClassFilterPipe,
        AnnouncementsService,
        CalendarService,
        CmsService,
        CareersService,
        PointsTrackingService,
        CanvasService
    ]
})
export class CoreModule {
    constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
        if (parentModule) {
            throw new Error('CoreModule is already loaded. Import it in the AppModule only.');
        }
    }

    static forRoot(): ModuleWithProviders<CoreModule> {
        return {
            ngModule: CoreModule,
            providers: [
                ProfileService,
                CmsService,
                EnrollmentsService,
                BiddingService,
                FacultyService,
                TagManagerService,
                LoggerService,
                UtilityService,                                
                DeactivateGuardService
            ]
        };
    }

}
